"use client"

import { useEffect, useState } from "react"
import { Loader2 } from "lucide-react"
import { ACTIVE_APP_ENVIRONMENT, APP_ENVIRONMENTS, canSelectAppEnvironment, type AppEnvironment } from "@/lib/appEnvironment"
import { switchAppEnvironment } from "@/services/appEnvironmentService"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"

export function AppEnvironmentSettings() {
  const [native, setNative] = useState(false)
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<AppEnvironment>(ACTIVE_APP_ENVIRONMENT)
  const [switching, setSwitching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => setNative(canSelectAppEnvironment()), [])
  if (!native) return null

  async function applyEnvironment() {
    if (switching || selected === ACTIVE_APP_ENVIRONMENT) return
    setSwitching(true)
    setError(null)
    try {
      await switchAppEnvironment(selected)
    } catch {
      setError("Não foi possível salvar o ambiente. Tente novamente.")
      setSwitching(false)
    }
  }

  return (
    <>
      <Button type="button" variant="ghost" size="sm" className="mt-1 text-xs text-muted-foreground" onClick={() => { setSelected(ACTIVE_APP_ENVIRONMENT); setError(null); setOpen(true) }}>
        Alterar ambiente
      </Button>
      <Dialog open={open} onOpenChange={(value) => { if (!switching) setOpen(value) }}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ambiente de comunicação</DialogTitle>
            <DialogDescription>Escolha o ambiente que o aplicativo vai acessar. Ao aplicar, o app será reiniciado nesta tela para você entrar no ambiente escolhido.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <fieldset disabled={switching} className="space-y-2">
              <legend className="mb-2 text-sm font-medium">Ambiente</legend>
              {(Object.keys(APP_ENVIRONMENTS) as AppEnvironment[]).map((environment) => (
                <Label key={environment} htmlFor={`environment-${environment}`} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 ${selected === environment ? "border-primary bg-primary/5" : "border-border"}`}>
                  <input id={`environment-${environment}`} type="radio" name="app-environment" value={environment} checked={selected === environment} onChange={() => setSelected(environment)} className="h-4 w-4 accent-primary" />
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold">{APP_ENVIRONMENTS[environment].label}{environment === ACTIVE_APP_ENVIRONMENT ? " · Atual" : ""}</span>
                    <span className="mt-1 block break-all text-xs font-normal text-muted-foreground">{APP_ENVIRONMENTS[environment].url}</span>
                  </span>
                </Label>
              ))}
            </fieldset>
            <p className="text-xs text-muted-foreground">Rascunhos e envios pendentes ficam guardados no ambiente de origem.</p>
            {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={switching} onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="button" disabled={switching || selected === ACTIVE_APP_ENVIRONMENT} onClick={() => void applyEnvironment()}>
              {switching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {switching ? "Reiniciando..." : "Aplicar e reiniciar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
